import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { OnboardingHeader } from 'routes/components/OnboardingHeader/OnboardingHeader'
import { OnboardingLayout } from 'routes/components/OnboardingLayout/OnboardingLayout'
import {
  OnboardingLayoutProvider,
  type OnboardingLayoutProviderProps,
} from 'routes/components/OnboardingLayout/hooks/useOnboardingLayoutContext/useOnboardingLayoutContext'
import { MetaTags } from 'routes/components/MetaTags/MetaTags'
import { useTrackOnboardingPageOnMount } from 'lib/hooks/useTrackOnboardingPageOnMount/useTrackOnboardingPageOnMount'
import { getURLSearch } from 'lib/navigation/helpers/getURLSearch/getURLSearch'
import { AppliedPromoCodeProvider } from 'routes/hooks/useAppliedPromoCodeContext/useAppliedPromoCodeContext'
import { useUrlBasedSnackbar } from 'routes/hooks/useUrlBasedSnackbar/useUrlBasedSnackbar'
import { withPageErrorRecovery } from 'lib/hocs/withPageErrorRecovery/withPageErrorRecovery'
import { LogInLink } from '../components/LogInLink/LogInLink'
import type { GetSourceParams } from '../helpers/getSource/getSource'
import { useProductForm } from './hooks/useProductForm/useProductForm'
import { type ProductPage_data } from './ProductPage.graphql'
import { ApplyPromoCodeLink } from './components/ApplyPromoCodeLink/ApplyPromoCodeLink'
import { ProductPageForm } from './components/ProductPageForm/ProductPageForm'
import * as $ from './ProductPage.styled'
import { Testimonials } from './components/Testimonials/Testimonials'
import { BusinessTypeEnum, ReferralTypeEnum } from 'gql/types'

export interface ProductPageProps
  extends Partial<GetSourceParams>,
    Pick<OnboardingLayoutProviderProps, 'interestedIn' | 'referrer'> {
  email: string | null
  invitationCode: string | null
  accountingFirmId: string | null
  promoCodeQueryString: string | null
  promotionCode: ProductPage_data['payerInvitation']['promotionCode']
  businessType: BusinessTypeEnum | null
}

export const ProductPage = withPageErrorRecovery<ProductPageProps>(
  ({
    email,
    referrer,
    interestedIn,
    promotionCode,
    invitationCode,
    accountingFirmId,
    promoCodeQueryString,
    businessType,
    ...sourceParams
  }) => {
    useUrlBasedSnackbar()
    useTrackOnboardingPageOnMount({
      interestedIn,
      step: 'Product Interest',
      ...(sourceParams.referred_by && { referred_by: sourceParams.referred_by }),
    })
    const businessTypeWithFallback = businessType ?? BusinessTypeEnum.PAYER
    const router = useRouter()
    const { t } = useTranslation()
    const form = useProductForm({ ...sourceParams, email, interestedIn, businessType: businessTypeWithFallback })
    const title = t('onboarding::product::title_v2')
    const subtitle = t('onboarding::product::subtitle_v2')
    const promoCode = promotionCode ? { ...promotionCode.coupon, applied: true, id: promotionCode.id } : null

    return (
      <$.Root>
        <AppliedPromoCodeProvider promoCode={promoCode}>
          <OnboardingLayoutProvider referrer={referrer} interestedIn={interestedIn}>
            <OnboardingLayout data-testid="product-page" businessType={businessTypeWithFallback}>
              <Head>
                <MetaTags
                  title={t('onboarding::product::meta::title')}
                  description={t('onboarding::product::meta::description')}
                />
              </Head>
              <Stack gap={3} alignItems="center">
                <OnboardingHeader maxWidth={490} title={title} subtitle={subtitle} />
                <FormProvider {...form}>
                  <ProductPageForm
                    {...sourceParams}
                    businessType={businessTypeWithFallback}
                    invitationCode={invitationCode}
                    accountingFirmId={accountingFirmId}
                    referralCode={referrer?.referralCode}
                  />
                </FormProvider>
                <Stack gap={1}>
                  {referrer?.type !== ReferralTypeEnum.AFFILIATE && (
                    <ApplyPromoCodeLink promoCodeQueryString={promoCodeQueryString} />
                  )}
                  <LogInLink
                    eventContext={{ step: 'Product interest' }}
                    query={
                      sourceParams.referred_by === 'shopify'
                        ? { next: `/integrations/shopify/connect?${getURLSearch(router.query)}` }
                        : undefined
                    }
                  />
                </Stack>
              </Stack>
            </OnboardingLayout>
          </OnboardingLayoutProvider>
        </AppliedPromoCodeProvider>
        <Testimonials />
      </$.Root>
    )
  },
)
